import React from 'react';
import {formatPrice, formatPhoneNumber} from "../../utilities";
import {format} from 'date-fns';

const MergeTagResolver = ({ property, mergeTag, defaultText, renderStyles }) => {
    switch (mergeTag) {
        case 'CURRENT_DATE_LONG':
            return format(new Date(), 'PPP');
        case 'CURRENT_DATE_SHORT':
            return format(new Date(), 'PP');
        case 'PROPERTY_MARKETING_NAME':
            return property.marketingName ?? property.name;
        case 'PROVIDER_NAME':
            return property.marketingProperty.provider.marketingName;
        case 'PROPERTY_SLUG':
            return property.slug;
        case 'PROPERTY_SUPPORT_PHONE':
            return <a style={renderStyles.a} href={`tel:${property.supportPhone}`}>{formatPhoneNumber(property.supportPhone)}</a>;
        case 'PROPERTY_SUPPORT_PHONE_1':
            return <a style={renderStyles.a} href={`tel:${property.supportPhone},1`}>{formatPhoneNumber(property.supportPhone)} (Option 1)</a>;
        case 'PROPERTY_SUPPORT_PHONE_2':
            return <a style={renderStyles.a} href={`tel:${property.supportPhone},2`}>{formatPhoneNumber(property.supportPhone)} (Option 2)</a>;
        case 'PROPERTY_SUPPORT_PHONE_3':
            return <a style={renderStyles.a} href={`tel:${property.supportPhone},3`}>{formatPhoneNumber(property.supportPhone)} (Option 3)</a>;
        case 'PROPERTY_SUPPORT_PHONE_4':
            return <a style={renderStyles.a} href={`tel:${property.supportPhone},4`}>{formatPhoneNumber(property.supportPhone)} (Option 4)</a>;
        case 'PROPERTY_SUPPORT_PHONE_5':
            return <a style={renderStyles.a} href={`tel:${property.supportPhone},5`}>{formatPhoneNumber(property.supportPhone)} (Option 5)</a>;
        case 'PROPERTY_SUPPORT_EMAIL':
            return <a target={"_blank"} style={renderStyles.a} href={`mailto:info@onboardtoday.com`}>info@onboardtoday.com</a>;
        case 'PROPERTY_BRAND_SUFFIX':
            return property.brandSuffix;
        case 'INTERNET_SPEED_MBPS':
            return property.internetSpeedMbps;
        case 'TV_CHANNEL_COUNT':
            return property.tvChannelCount;
        case 'PROPERTY_TRANSITION_DATE':
            let formattedTransitionDate = "";
            if (property.transitionDate) {
                let transitionDate = new Date(property.transitionDate);
                transitionDate.setHours(transitionDate.getHours() + 12);
                formattedTransitionDate = format(transitionDate, 'PP');
            }
            return formattedTransitionDate;
        case 'RESIDENT_CHARGE':
            return formatPrice(property.residentCharge / 100);
        case 'TV_RECEIVER_TAGLINE':
            let tagline = '';
            if (property.hdReceiverCount > 0)
                tagline += `(${property.hdReceiverCount}) HD Receiver${property.hdReceiverCount > 1 ? 's' : ''}`;
            if (property.hdReceiverCount > 0 && property.receiverCount > 0) {
                tagline += ', ';
            }
            if (property.receiverCount > 0)
                tagline += `(${property.hdReceiverCount}) Receiver${property.hdReceiverCount > 1 ? 's' : ''}`;
            return tagline;
        case 'SERVICE_CATEGORIES':
            let products = [];
            if (property.products?.find(p => ["wirelessinternet", "wiredinternet"].includes(p.productType.toLowerCase()) && p.deletedAt === null)) {
                products.push("internet");
            }
            if (property.products?.find(p => ["tv", "tvstreaming"].includes(p.productType.toLowerCase()) && p.deletedAt === null)) {
                products.push("TV");
            }
            if (property.products?.find(p => ["iot"].includes(p.productType.toLowerCase()) && p.deletedAt === null)) {
                products.push("smart home");
            }
            switch (products.length) {
                case 0:
                    return defaultText ?? null;
                case 1:
                    return products[0];
                case 2:
                    return products[0] + " & " + products[1];
                default:
                    return `${products.slice(0, -1).join(", ")}, and ${products[products.length - 1]}`;
            }
        case 'PRODUCTS_LIST':
            return property.products
                ? property.products
                    .filter(p => p.deletedAt === null)
                    .sort((a, b) => a.displayProductOrder - b.displayProductOrder)
                    .map((product, index) => (
                        <React.Fragment key={index}>
                            <b>{product.title}</b>
                            <br />
                            <span>{product.description}</span>
                            <br /><br />
                        </React.Fragment>
                    ))
                : null;
        case 'INCENTIVES_LIST':
            let now = new Date();
            let priceMatchExpirationDate = new Date(property.priceMatchEndAt ?? property.transitionDate);
            let enablePriceMatches = property.enablePriceMatches && priceMatchExpirationDate.getTime() > now.getTime();
            let reimbursementExpirationDate = new Date(property.reimbursementEndAt ?? property.transitionDate);
            let enableReimbursements = property.enableReimbursements && reimbursementExpirationDate.getTime() > now.getTime();
            let freePeriodExpirationDate = new Date(property.transitionDate);
            let enableFreePeriod = property.enableFreePeriod && freePeriodExpirationDate.getTime() > now.getTime();
            if (!enableFreePeriod && !enablePriceMatches && !enableReimbursements) {
                return "Unfortunately, early enrollment incentives are no longer available. However, you can still enjoy a reduced monthly rate for your tech amenities.";
            }

            return (
                <React.Fragment>
                    { enableFreePeriod &&
                        <React.Fragment>
                            <span>
                                <b>Free for {property.freePeriodDays} days:</b> {property.freePeriodDisplayMessage}
                            </span>
                            <br />
                            <br />
                        </React.Fragment>
                    }
                    { enablePriceMatches &&
                        <React.Fragment>
                            <span>
                                <b>Price Match:</b> If you are paying less than {formatPrice(property.residentCharge / 100)}
                                        &nbsp;for the same or similar services, you may be eligible for a temporary service price match. Once
                                registered, you'll see an option to request a price match. Upload a copy of your provider invoice
                                and give us a couple of days to get back to you!
                            </span>
                            <br />
                            <br />
                        </React.Fragment>
                    }
                    { enableReimbursements &&
                        <React.Fragment>
                            <span>
                                <b>No Cost Transition:</b> Facing an early termination fee from your current provider? You may be
                                fully reimbursed by Onboard. When you cancel your retail service and receive a final invoice with
                                the termination fee clearly listed, submit your request online and a check will be sent within
                                5-7 business days.
                            </span>
                        </React.Fragment>
                    }
                </React.Fragment>
            );
        case 'UNIT_SIGNUP_LINK':
            let signupLink = property.unitSignupLink;
            if (signupLink !== null) {
                // Make sure the UI does not treat this as a relative path
                if (!signupLink.startsWith('http://') && !signupLink.startsWith('https://')) {
                    signupLink = `https://${signupLink}`;
                }

                return <a href={signupLink} target="_blank" rel="noopener noreferrer">{property.unitSignupLink}</a>
            }
        default:
            // TODO - Warn us if default text is null
            //  (meaning the value is required to be resolved)
            return defaultText ? defaultText : null;
    }
}

export default MergeTagResolver;
